import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import APIService from '../../services/APIService';
import GAService from '../../services/GAService';
import {
  oidcCodeExchangePayload, storeSession, consumeReturnTo, consumeAndValidateOAuthState,
  consumeStoredPKCECodeVerifier, isSignupOAuthState, isLoggedIn, getLoginURL,
} from '../../common/auth';
import PageMeta from '../common/PageMeta';

// /oidc/login — Keycloak redirects here with ?code=; port of oclmap's
// OIDLoginCallback adapted to BrowserRouter: exchange the code via the OCL
// API, store the session, cache the user profile, then return home.
const OIDLoginCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [error, setError] = React.useState(null);
  const [notice, setNotice] = React.useState(null);

  React.useEffect(() => {
    // response_type=code (no id_token) always uses query params for the callback;
    // id_token itself now comes from the token-exchange response body below, not the URL.
    const hashParams = new URLSearchParams((window.location.hash || '').replace(/^#/, ''));
    const code = searchParams.get('code') || hashParams.get('code');
    const state = searchParams.get('state') || hashParams.get('state');
    if (!code) {
      // No code: direct visit or the Keycloak post-logout landing — return
      // to the page the visitor was on (or home).
      navigate(consumeReturnTo(), { replace: true });
      return;
    }
    const isStateValid = consumeAndValidateOAuthState(state);
    const codeVerifier = consumeStoredPKCECodeVerifier();
    if (!isStateValid || !codeVerifier) {
      // Keycloak finished a sign-in this tab didn't start: the email-verification
      // or password-reset link opened in a new tab, or an old callback URL. With
      // no PKCE verifier here the code can't be redeemed, so drop it and let the
      // visitor sign in normally. Sign-up, verification and sign-in stay separate.
      const isSignup = isSignupOAuthState(state);
      if (isSignup)
        GAService.recordSignupVerified();
      if (isLoggedIn())
        navigate(consumeReturnTo(), { replace: true });
      else
        setNotice(isSignup ?
          { severity: 'success', text: t('auth.email_verified_sign_in') } :
          { severity: 'info', text: t('auth.sign_in_to_continue') });
      return;
    }
    APIService.post('/users/oidc/code-exchange/', oidcCodeExchangePayload(code, codeVerifier)).then(response => {
      const accessToken = response.data && response.data.access_token;
      if (!accessToken) {
        GAService.clearSignupFlow();
        setError((response.data && response.data.error_description) || t('auth.sign_in_failed'));
        return;
      }
      storeSession(accessToken, response.data.id_token);
      GAService.recordSignupComplete();
      APIService.get('/user/', { includeSubscribedOrgs: true, includeFollowing: true }).then(profile => {
        if (profile.status === 200)
          localStorage.setItem('user', JSON.stringify(profile.data));
        // Full navigation (not navigate()) so the persistent header
        // re-renders with the signed-in state; back to the page the
        // sign-in started from.
        window.location.replace(consumeReturnTo());
      });
    });
  }, []);

  return (
    <Container maxWidth="sm" sx={{ py: 12, textAlign: 'center' }}>
      <PageMeta title={t('auth.signing_in')} robots="noindex, follow" />
      {notice ? (
        <Alert
          severity={notice.severity}
          action={
            <Button color="inherit" size="small" onClick={() => getLoginURL().then(url => { window.location.href = url; })}>
              {t('nav.sign_in')}
            </Button>
          }
        >
          {notice.text}
        </Alert>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <Box>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography sx={{ color: 'surface.contrastText' }}>{t('auth.signing_in')}</Typography>
        </Box>
      )}
    </Container>
  );
};

export default OIDLoginCallback;
